import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import numidaWingmark from './assets/numida-wingmark.png';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectFormPage from './pages/ProjectFormPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import SuiteFormPage from './pages/SuiteFormPage';
import SuiteDetailPage from './pages/SuiteDetailPage';
import TestCaseFormPage from './pages/TestCaseFormPage';
import TestCaseDetailPage from './pages/TestCaseDetailPage';
import TestExecutionPage from './pages/TestExecutionPage';
import TestRunResultsPage from './pages/TestRunResultsPage';
import ReportsPage from './pages/ReportsPage';
import Navigation from './components/Navigation';
import AppModal from './components/AppModal';
import { isCognitoMode, cognitoExchangeCode, API_BASE_URL } from './api';

// Roles allowed to read the project / suite / case hierarchy.
const HIERARCHY_READ_ROLES = ['admin', 'qa', 'dev', 'product'];

/** Keeps the old /project/:projectId links working after the /projects move. */
function LegacyProjectRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}`} replace />;
}

/**
 * Guards the OAuth callback so it runs exactly once per page load. The module
 * scope is fresh on every real callback (it arrives as a full-page redirect),
 * but survives React StrictMode's dev remount — which would otherwise fire the
 * effect twice and consume the post-login redirect on the first pass.
 */
let oauthCallbackHandled = false;

/**
 * Reads and clears the path ProtectedRoute saved before bouncing to /login,
 * so a shared link (e.g. /test-runs/42) survives the login + OAuth round-trip.
 */
function takePostLoginRedirect() {
  try {
    const saved = sessionStorage.getItem('postLoginRedirect');
    if (saved) {
      sessionStorage.removeItem('postLoginRedirect');
      return saved;
    }
  } catch (_) { /* ignore */ }
  return '/';
}

/** Handles OAuth callback — supports both Cognito (code exchange) and local JWT. */
function AuthCallback({ onLogin }) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Completing sign in…');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    const code = params.get('code');
    const token = params.get('token');
    const refreshToken = params.get('refreshToken');

    if (err) {
      setError(decodeURIComponent(err));
      return;
    }

    // The token/code exchange consumes a one-time code and the stored
    // post-login redirect, so it must not repeat on StrictMode's dev remount.
    if (oauthCallbackHandled) return;

    if (isCognitoMode() && code) {
      oauthCallbackHandled = true;
      // Cognito OAuth flow: exchange authorization code for tokens
      setStatus('Exchanging authorization code…');
      cognitoExchangeCode(code)
        .then((tokens) => {
          if (!tokens.id_token) throw new Error('No id_token in response');
          localStorage.setItem('token', tokens.id_token);
          if (tokens.refresh_token) {
            localStorage.setItem('refreshToken', tokens.refresh_token);
          }
          // Sync user to local DB
          return fetch(`${API_BASE_URL}/auth/sync`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${tokens.id_token}` },
          });
        })
        .then((r) => {
          if (!r.ok) throw new Error('User sync failed');
          return r.json();
        })
        .then((userData) => {
          onLogin(localStorage.getItem('token'), userData);
          navigate(takePostLoginRedirect());
        })
        .catch((err) => {
          console.error('Cognito auth error:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          navigate('/login?error=' + encodeURIComponent('Sign-in failed. Please try again.'));
        });
      return;
    }

    if (!token) {
      navigate('/login');
      return;
    }

    oauthCallbackHandled = true;

    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    // Local OAuth flow: verify JWT
    fetch(`${API_BASE_URL}/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Verification failed');
        return r.json();
      })
      .then((userData) => {
        onLogin(token, userData);
        navigate(takePostLoginRedirect());
      })
      .catch(() => {
        localStorage.removeItem('refreshToken');
        navigate(
          '/login?error=' +
            encodeURIComponent('Sign-in failed. Please try again.')
        );
      });
  }, [onLogin, navigate]);

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '16px' }}>
        <p style={{ color: 'var(--danger)' }}>{error}</p>
        <a href="/login" className="btn btn-primary" style={{ textDecoration: 'none' }}>Back to Login</a>
      </div>
    );
  }

  return <div className="loading">{status}</div>;
}

/** Redirects to / if the user’s role is not in allowedRoles. */
function ProtectedRoute({ user, allowedRoles, children }) {
  const location = useLocation();
  if (!user) {
    // Remember where the user was headed so a shared link survives the
    // login + OAuth round-trip (see AuthCallback).
    try {
      sessionStorage.setItem(
        'postLoginRedirect',
        location.pathname + location.search
      );
    } catch (_) { /* ignore */ }
    return <Navigate to="/login" />;
  }
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }
  return children;
}

function ProfileDropdown({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogoutClick = () => {
    setOpen(false);
    setShowLogoutConfirm(true);
  };

  return (
    <>
      <div className="profile-dropdown-wrap" ref={ref}>
        <button
          className="topbar-user"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label="Profile menu"
        >
          <div className="topbar-avatar" aria-hidden="true">
            {user?.username?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="topbar-user-info">
            <strong>{user?.username}</strong>
            <span>{user?.role || 'staff'}</span>
          </div>
          <span className="topbar-chevron" aria-hidden="true">{open ? '▲' : '▼'}</span>
        </button>

        {open && (
          <div className="profile-dropdown">
            <div className="profile-dropdown-header">
              <div className="profile-avatar-lg">
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="profile-dropdown-info">
                <strong>{user?.username}</strong>
                <span>{user?.email || 'No email on record'}</span>
              </div>
            </div>

            <div className="profile-dropdown-details">
              <div className="profile-detail-row">
                <span className="profile-detail-label">Username</span>
                <span className="profile-detail-value">{user?.username}</span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Role</span>
                <span className="profile-detail-value">{user?.role || 'Tester'}</span>
              </div>
              <div className="profile-detail-row">
                <span className="profile-detail-label">Status</span>
                <span className="profile-detail-value profile-status-active">● Active</span>
              </div>
            </div>

            <div className="profile-dropdown-footer">
              <button className="btn btn-danger btn-small profile-logout-btn" onClick={handleLogoutClick}>
                Logout
              </button>
            </div>
          </div>
        )}
      </div>

      <AppModal
        open={showLogoutConfirm}
        title="Log out?"
        message="Are you sure you want to log out?"
        onClose={() => setShowLogoutConfirm(false)}
        actions={[
          { label: 'Not now', variant: 'secondary', onClick: () => setShowLogoutConfirm(false) },
          { label: 'Yes', variant: 'danger', onClick: () => { setShowLogoutConfirm(false); onLogout(); } }
        ]}
      />
    </>
  );
}

function AppLayout({ isAuthenticated, user, onLogout, children }) {
  const location = useLocation();

  return (
    <div className={isAuthenticated ? 'app-shell' : 'app-shell app-shell-public'}>
      {isAuthenticated && (
        <header className="app-topbar">
          <div className="topbar-brand">
            <div className="topbar-logo" aria-hidden="true">
              <img src={numidaWingmark} alt="" />
            </div>
            <div className="topbar-brand-stack">
              <span className="topbar-brand-name">Numida</span>
              <span className="topbar-brand-subtitle">Test Management</span>
            </div>
          </div>
          <ProfileDropdown user={user} onLogout={onLogout} />
        </header>
      )}

      <div className="app-body">
        {isAuthenticated && <Navigation user={user} onLogout={onLogout} />}
        <main className="app-main">
          <img src={numidaWingmark} alt="" className="app-main-watermark" aria-hidden="true" />
          <div className="page-stage" key={location.pathname}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <AppLayout isAuthenticated={isAuthenticated} user={user} onLogout={handleLogout}>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />}
          />
          <Route
            path="/"
            element={isAuthenticated ? <DashboardPage user={user} /> : <Navigate to="/login" />}
          />
          {/* Legacy redirects — keep shared/bookmarked links alive. */}
          <Route path="/project/:projectId" element={<LegacyProjectRedirect />} />
          <Route path="/test-cases" element={<Navigate to="/projects" replace />} />

          <Route
            path="/projects"
            element={
              <ProtectedRoute user={user} allowedRoles={HIERARCHY_READ_ROLES}>
                <ProjectsPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'qa', 'dev']}>
                <ProjectFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <ProtectedRoute user={user} allowedRoles={HIERARCHY_READ_ROLES}>
                <ProjectDetailPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/suites/new"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'qa']}>
                <SuiteFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/suites/:suiteId"
            element={
              <ProtectedRoute user={user} allowedRoles={HIERARCHY_READ_ROLES}>
                <SuiteDetailPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/suites/:suiteId/cases/new"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'qa']}>
                <TestCaseFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/suites/:suiteId/cases/:caseId"
            element={
              <ProtectedRoute user={user} allowedRoles={HIERARCHY_READ_ROLES}>
                <TestCaseDetailPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/suites/:suiteId/cases/:caseId/edit"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'qa']}>
                <TestCaseFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-execution"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'qa', 'dev', 'product']}>
                <TestExecutionPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-runs/:runId"
            element={
              <ProtectedRoute user={user}>
                <TestRunResultsPage user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'qa', 'dev', 'product', 'staff']}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} />} />
        </Routes>
      </AppLayout>
    </Router>
  );
}

export default App;
